import type {Icon} from '@primer/octicons-react'
import {ArrowUpRightIcon} from '@primer/octicons-react'
import {ProgressBar, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import {createContext, type MouseEvent, type PropsWithChildren, type ReactNode, use, useId} from 'react'
import styles from './MiniMergeSections.module.css'
import {MiniStatusIcon, type MiniStatusIconProps} from './MiniStatusIcon'
import {Link, type To} from '@github-ui/react-router'
import {isForYouRefreshEnabled} from '../../shared/for-you-feature-flags'

type MiniMergeStatusItemProps = PropsWithChildren<{
  'aria-label': string
  'aria-expanded'?: boolean
  isFirst?: boolean
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  to?: To
}>

const MiniStatusLabelIdContext = createContext<string | undefined>(undefined)

function MiniMergeStatusItem({
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  children,
  isFirst = false,
  onClick,
  to,
}: MiniMergeStatusItemProps) {
  const labelId = useId()
  const actionId = useId()
  const isRefreshEnabled = isForYouRefreshEnabled()

  if (!onClick && !to) {
    return (
      <MiniStatusLabelIdContext value={labelId}>
        <section
          aria-label={ariaLabel}
          className={clsx(
            styles.MiniStatus,
            isRefreshEnabled && styles.MiniStatusRefresh,
            isRefreshEnabled && isFirst && styles.MiniStatusFirst,
          )}
          data-testid="mini-status"
        >
          {children}
        </section>
      </MiniStatusLabelIdContext>
    )
  }

  return (
    <MiniStatusLabelIdContext value={labelId}>
      <div
        className={clsx(
          styles.MiniStatus,
          styles.MiniStatusInteractive,
          isRefreshEnabled && styles.MiniStatusRefresh,
          isRefreshEnabled && isFirst && styles.MiniStatusFirst,
          ariaExpanded && styles.MiniStatusExpanded,
        )}
        data-testid="mini-status"
      >
        {to ? (
          <Link
            to={to}
            aria-labelledby={`${labelId} ${actionId}`}
            aria-expanded={ariaExpanded}
            className={styles.MiniStatusButton}
          >
            <VisuallyHidden id={actionId}>{ariaLabel}</VisuallyHidden>
          </Link>
        ) : (
          <button
            aria-labelledby={`${labelId} ${actionId}`}
            aria-expanded={ariaExpanded}
            className={styles.MiniStatusButton}
            onClick={onClick}
            type="button"
          >
            <VisuallyHidden id={actionId}>{ariaLabel}</VisuallyHidden>
          </button>
        )}
        {children}
      </div>
    </MiniStatusLabelIdContext>
  )
}

function LabelRow({
  children,
  icon,
  showNavigationIcon = false,
}: {
  children: ReactNode
  icon?: Icon
  showNavigationIcon?: boolean
}) {
  const IconComponent = icon ?? (showNavigationIcon ? ArrowUpRightIcon : undefined)

  return (
    <div className={styles.MiniStatusLabelRow}>
      {children}
      {IconComponent ? <IconComponent aria-hidden="true" /> : null}
    </div>
  )
}
type LabelProps = PropsWithChildren<{
  icon?: MiniStatusIconProps['type']
  isUnavailable?: boolean
  successful?: number
  status?: string
  total?: number
}>

function Label({icon, isUnavailable = false, successful, status, total, children}: LabelProps) {
  const labelId = use(MiniStatusLabelIdContext)
  const hasRatio = successful !== undefined && total !== undefined
  const iconType = isUnavailable ? 'empty' : icon
  const count = hasRatio ? (isUnavailable || total === 0 ? 0 : successful) : undefined

  return (
    <span id={labelId} className={clsx('d-flex flex-items-center gap-1', styles.MiniStatusLabel)}>
      {iconType ? <MiniStatusIcon type={iconType} /> : null}
      <strong className={styles.MiniStatusLabelText}>{children}</strong>
      {count !== undefined ? (
        <span className={clsx('fgColor-muted', styles.MiniStatusRatio)} data-testid="mini-status-count">
          {total === 0 ? count : `${count}/${total}`}
        </span>
      ) : null}
      {status ? <VisuallyHidden>{status}</VisuallyHidden> : null}
    </span>
  )
}

type ProgressBaseProps = {
  'aria-label': string
  successful: number
  total: number
}

type LegacyProgressProps = ProgressBaseProps & {
  isUnavailable: boolean
  unavailableLabel: string
}

function Progress(props: LegacyProgressProps) {
  if (props.isUnavailable) {
    return (
      <div
        className={clsx(styles.MiniStatusProgress, styles.MiniStatusUnavailableProgress)}
        data-testid="mini-status-unavailable-progress"
      >
        <span className="sr-only">{props.unavailableLabel}</span>
      </div>
    )
  }

  const isEmpty = props.total === 0
  return (
    <ProgressBar
      aria-label={props['aria-label']}
      aria-valuenow={isEmpty ? 0 : undefined}
      bg={isEmpty ? 'neutral.emphasis' : undefined}
      className={styles.MiniStatusProgress}
      progress={isEmpty ? 100 : (props.successful / props.total) * 100}
    />
  )
}

MiniMergeStatusItem.LabelRow = LabelRow
MiniMergeStatusItem.Label = Label
MiniMergeStatusItem.Progress = Progress

export {MiniMergeStatusItem}
